"use client"

import { getCookie } from '../cookies';
import { enabledProviders } from './registry'
import datadog from "@ilg-core/analytics/datadog";
import { eventVersionMap, updateEventVersionMap, markVersionsReady, versionsReady } from './event-version-map'
import { env } from 'next-runtime-env';

type EventsData = { EventName: string; Version: string }[];
const eventsAssetsUrl = env('NEXT_PUBLIC_EVENTS_ASSETS_URL');

// No remote URL: unblock versionsReady now so logEvent() always falls back to
// bundled versions immediately rather than waiting indefinitely for a fetch
// that will never be initiated.
if (!eventsAssetsUrl) {
  markVersionsReady()
}

// Max time early version-sensitive events wait for the remote versions before
// falling back to the bundled values, so analytics never hangs on a slow fetch.
// Configurable via NEXT_PUBLIC_EVENTS_ASSETS_TIMEOUT_MS (milliseconds); defaults to 3000.
const DEFAULT_REMOTE_VERSIONS_TIMEOUT_MS = 3000;
const configuredVersionsTimeout = Number(env('NEXT_PUBLIC_EVENTS_ASSETS_TIMEOUT_MS'));
const REMOTE_VERSIONS_TIMEOUT_MS =
  Number.isFinite(configuredVersionsTimeout) && configuredVersionsTimeout > 0
    ? configuredVersionsTimeout
    : DEFAULT_REMOTE_VERSIONS_TIMEOUT_MS;

let eventVersionsFetchStarted = false;

// Called explicitly after the access token is retrieved (see Providers.tsx) so
// the fetch is tied to the auth lifecycle rather than module load. The guard
// prevents duplicate fetches if the module ever evaluates more than once.
export function initRemoteEventVersions(): void {
  if (eventVersionsFetchStarted) return
  eventVersionsFetchStarted = true

  if (typeof window !== 'undefined' && eventsAssetsUrl) {
    const timeout = new Promise<void>((resolve) => {
      setTimeout(resolve, REMOTE_VERSIONS_TIMEOUT_MS);
    });
    const load = fetch(eventsAssetsUrl)
      .then(r => r.json())
      .then((data: EventsData) => {
        updateEventVersionMap(data)
      })
      .catch((error) => {
        datadog.log.error('Failed to load remote event versions assets', {
          url: eventsAssetsUrl,
          cause: error instanceof Error ? error.cause : undefined,
          statusMessage: error instanceof Error ? error.message : String(error),
        })
      })
    // Unblock early events as soon as the versions load or the timeout elapses.
    void Promise.race([load, timeout]).finally(() => markVersionsReady())
  } else {
    // No remote source (SSR or unset URL): proceed with the bundled versions.
    markVersionsReady()
  }
}

type OneTrustDetails = any;

enum ONE_TRUST_CONSENTS {
  COOKIE_KEY = "oneTrustConsents",
  STRICTLY_NECESSARY_COOKIES = 'C0001',
  PERFORMANCE_COOKIES = 'C0002',
  FUNCTIONAL_COOKIES = 'C0003',
  TARGETING_COOKIES = 'C0004',
}

function waitForGlobal(key: string, callback: () => void): void {
  if (typeof window !== 'undefined') {
    if ((window as any)[key]) {
      callback()
    } else {
      setTimeout(() => {
        waitForGlobal(key, callback)
      }, 250)
    }
  }
}

function isWindowDefined() {
  return typeof window !== "undefined";
}

function setOneTrustConsents(detail: OneTrustDetails): void {
  let cookieDomain = "";
  if (isWindowDefined()) {
    const hostname = window.location.hostname.toLowerCase();
    if (hostname === "credability.co.uk" || hostname.endsWith(".credability.co.uk")) {
      cookieDomain = "credability.co.uk";
    } else if (hostname === "credability-test.co.uk" || hostname.endsWith(".credability-test.co.uk")) {
      cookieDomain = "credability-test.co.uk";
    }
  }
  // UTC is used to make the cookie expiration time timezone-independent, aligning with OneTrust's OptanonConsent cookie logic
  const oneYearFromNow = new Date(
    new Date().getTime() + 365 * 24 * 60 * 60 * 1000
  ).toUTCString();

  const cookieDomainParam = cookieDomain ? `domain=${cookieDomain}` : "";
  document.cookie = `${ONE_TRUST_CONSENTS.COOKIE_KEY}=${JSON.stringify(
    detail
  )};expires=${oneYearFromNow};${cookieDomainParam};path=/;samesite=strict;`;
}

function notifyProvidersOfConsent(consented: boolean) {
  for (const provider of enabledProviders) {
    try {
      provider.onConsentChanged?.(consented)
    } catch (error) {
      const normalizedError = error instanceof Error ? error : undefined
      const normalizedCause =
        normalizedError && typeof normalizedError.cause === 'object' && normalizedError.cause !== null
          ? (normalizedError.cause as { url?: URL | RequestInfo; status?: number })
          : undefined
      datadog.log.error('[NOTIFY_PROVIDERS]', {
        error: normalizedError
          ? {
              message: normalizedError.message,
              stack: normalizedError.stack,
            }
          : undefined,
        cause: normalizedCause,
        statusMessage: normalizedError?.message ?? String(error),
      })
    }
  }
}

function waitForConsent(callback: () => void) {
  waitForGlobal('OneTrust', () => {
    if (typeof window !== 'undefined') {
      (window as any)?.['OneTrust']?.OnConsentChanged?.(
        ({ detail }: { detail: any }) => {
          const consented = detail.includes(
            ONE_TRUST_CONSENTS.TARGETING_COOKIES
          )
          setOneTrustConsents(detail);

          if (detail.includes(ONE_TRUST_CONSENTS.PERFORMANCE_COOKIES)) {
            if (window.clarity) {
              window.clarity('consentv2', {
                ad_storage: 'granted',
                analytics_storage: 'granted',
              })
            }
          } else {
            if (window.clarity) {
              window.clarity('consentv2', {
                ad_storage: 'denied',
                analytics_storage: 'denied',
              })
            }
          }
          if (consented) {
            callback()
          }
        }
      )
    }
  })
}

const logWrapper = (callback: () => void) => {
  if (typeof window !== 'undefined') {
    const consented = !!getCookie("oneTrustConsents")
    if (consented) {
      notifyProvidersOfConsent(consented)
      callback()
    } else {
      waitForConsent(() => callback())
    }
  }
}

const logEvent = (
  eventName: string,
  eventParameters?: { [key: string]: string | number },
) => {
  // Register the consent handler immediately (don't gate it on versionsReady, or
  // a consent grant during the readiness window would be missed and the event
  // blocked). Only the version resolution + emission waits for the remote
  // versions, so early events (e.g. logged_in) don't fall back to stale versions.
  logWrapper(() => {
    void versionsReady.then(() => {
      const eventVersion = eventVersionMap[eventName] ?? '1.0'
      for (const provider of enabledProviders) {
        try {
          provider.logEvent(
            eventName,
            { ...eventParameters },
            eventVersion,
          )
        } catch (error) {
          const normalizedError = error instanceof Error ? error : undefined
          const normalizedCause =
            normalizedError && typeof normalizedError.cause === 'object' && normalizedError.cause !== null
              ? (normalizedError.cause as { url?: URL | RequestInfo; status?: number })
              : undefined
          datadog.log.error('Log event failed', {
            error: normalizedError
              ? {
                  message: normalizedError.message,
                  stack: normalizedError.stack,
                }
              : undefined,
            provider,
            cause: normalizedCause,
            statusMessage: normalizedError?.message ?? String(error),
          })
        }
      }
    })
  })
}

const logError = (
  eventName: string,
  eventParameters?: Record<string, string | number>
) => {
  logWrapper(() => {
    for (const provider of enabledProviders) {
      try {
        provider.logError(eventName, eventParameters)
      } catch {
        // Analytics is fire-and-forget; never break the app
      }
    }
  })
}

export const Analytics = {
  logEvent,
  logError,
}
